// src/features/wf3/components/W3Page.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
} from "react";
import styles from "./W3Page.module.css";

import { listW3, W3_LOCAL_EVENT } from "../api";
import {
  connectRealtime,
  on as rtOn,
  off as rtOff,
  RT,
  joinRoom,
  leaveRoom,
  RT_META,
  onRealtimeMeta,
  offRealtimeMeta,
} from "../../../api/realtime";
import { makeWorkflowAllRoom, makeWorkflowDayRoom } from "../../../api/realtime.rooms";
import W3FichaModal from "../popup/W3FichaModal";
import W3NovaProgramar from "../popup/W3NovaProgramar";
import W3Card from "./W3Card";

/* Datas (helpers sem libs) */
import { toKeyYYYYMMDD, todayKeyBR } from "../../../utils/datesBR";

/* Helpers seguros */
const norm = (v) => (v == null ? "" : String(v).trim());
const normUp = (v) => norm(v).toUpperCase();
const uniq = (arr) => Array.from(new Set((arr || []).map(norm).filter(Boolean)));
const normalizeGerenciaLabel = (value) => {
  const raw = normUp(value).replace(/\s+/g, " ");
  if (raw === "UP PORTO") return "UP PORTOS";
  return raw;
};
const normalizeSearchText = (value) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
const DATE_WEEKDAYS = ["D", "S", "T", "Q", "Q", "S", "S"];

function parseYMD(ymd) {
  if (!ymd || typeof ymd !== "string") return null;
  const [yStr, mStr, dStr] = ymd.split("-");
  const y = Number(yStr);
  const m = Number(mStr);
  const d = Number(dStr);
  if (!y || !m || !d) return null;
  return new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
}

function formatYMD(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  const d = String(date.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function startOfMonthUTC(date) {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1, 12, 0, 0));
}

function addDaysUTC(date, days) {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + days, 12, 0, 0));
}

function addMonthsUTC(date, months) {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + months, 1, 12, 0, 0));
}

function toBRDate(ymd) {
  const dt = parseYMD(ymd);
  if (!dt || Number.isNaN(dt.getTime())) return ymd || "";
  return new Intl.DateTimeFormat("pt-BR", {
    timeZone: "America/Sao_Paulo",
  }).format(dt);
}

function monthLabelPtBR(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const text = new Intl.DateTimeFormat("pt-BR", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/* Ordem de status para filtros */
const ORDER_STATUS = [
  "em_andamento",
  "primeira_previa",
  "segunda_previa",
  "executado",
  "reprogramar",
  "finalizado",
];

/* Rótulos bonitos de status */
const STATUS_LABEL = {
  em_andamento: "Não Iniciado",
  primeira_previa: "Primeira Prévia",
  segunda_previa: "Segunda Prévia",
  executado: "Executado",
  reprogramar: "Reprogramar",
  finalizado: "Finalizado",
};

function buildDayRangeBR(keyYYYYMMDD) {
  const k = toKeyYYYYMMDD(keyYYYYMMDD);
  if (!k) return { inicioBR: "", fimBR: "" };
  return { inicioBR: `${k} 00:00:00`, fimBR: `${k} 23:59:59` };
}

function normalizeW3RowForState(row) {
  if (!row || typeof row !== "object") return null;
  const dayKey = toKeyYYYYMMDD(row.data || row.day || row.data_key || row.dataKey);
  if (dayKey) {
    return {
      ...row,
      data: dayKey,
    };
  }
  return { ...row };
}

function extractW3FromMsg(msg) {
  if (!msg || typeof msg !== "object") return null;
  if (msg?.w3 && typeof msg.w3 === "object") return msg.w3;
  if (msg?.row && typeof msg.row === "object") return msg.row;
  if (msg?.data && typeof msg.data === "object" && (msg.data.codigo_unico || msg.data.id)) return msg.data;

  const payload = msg?.payload;
  if (payload && typeof payload === "object") {
    if (payload?.w3 && typeof payload.w3 === "object") return payload.w3;
    if (payload?.row && typeof payload.row === "object") return payload.row;
    if (payload?.data && typeof payload.data === "object" && (payload.data.codigo_unico || payload.data.id)) {
      return payload.data;
    }
    if (payload.codigo_unico || payload.id) return payload;
  }

  if (msg?.codigo_unico || msg?.id) return msg;
  return null;
}

function extractW3Codigo(msg) {
  if (!msg || typeof msg !== "object") return null;
  return (
    msg?.codigo_unico ||
    msg?.codigo ||
    msg?.id ||
    msg?.row?.codigo_unico ||
    msg?.row?.id ||
    msg?.w3?.codigo_unico ||
    msg?.w3?.id ||
    msg?.payload?.codigo_unico ||
    msg?.payload?.id ||
    msg?.payload?.row?.codigo_unico ||
    msg?.payload?.row?.id ||
    msg?.payload?.w3?.codigo_unico ||
    msg?.payload?.w3?.id ||
    null
  );
}

function extractW3Action(msg) {
  return String(
    msg?.action ||
      msg?.operacao ||
      msg?.kind ||
      msg?.event ||
      msg?.type ||
      msg?.payload?.action ||
      msg?.payload?.operacao ||
      ""
  );
}

/* Ícone "Filter Off" */
function FilterOffIcon({ size = 18, stroke = 2 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M3 5h18l-7 8v4l-4 3v-7L3 5z" />
      <path d="M3 3l18 18" />
    </svg>
  );
}

/* ================= MultiSelect (aceita string ou {value,label}) ================ */
function MultiSelect({ options = [], value, onChange, placeholder = "Todos" }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const selected = Array.isArray(value) ? value : value ? [value] : [];
  const setSelected = (next) => onChange(Array.isArray(value) ? next : next[0] || "");

  const label = selected.length
    ? `${selected.length} selecionado${selected.length > 1 ? "s" : ""}`
    : placeholder;

  useEffect(() => {
    const onDoc = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const getVal = (opt) => (typeof opt === "string" ? opt : opt?.value);
  const getLabel = (opt) => (typeof opt === "string" ? opt : opt?.label ?? opt?.value ?? "");

  const handleItem = (opt) => {
    const val = getVal(opt);
    const s = new Set(selected);
    if (s.has(val)) s.delete(val);
    else s.add(val);
    setSelected(Array.from(s));
  };

  const canClear = selected.length > 0;

  return (
    <div className={styles.msRoot} ref={rootRef}>
      <button
        className={`${styles.msBtn} ${open ? styles.open : ""}`}
        type="button"
        onClick={() => setOpen((v) => !v)}
      >
        <span className={styles.msText}>{label}</span>
        <span className={styles.caret}>▾</span>
      </button>

      {open && (
        <div className={styles.msPanel} onClick={(e) => e.stopPropagation()}>
          <div className={styles.msHeader}>
            <span style={{ fontWeight: 900 }}>Selecione</span>
          </div>

          <div className={styles.msListWrap}>
            <div className={styles.msList}>
              {options.map((opt) => {
                const val = getVal(opt);
                const lab = getLabel(opt);
                const id = `w3-ms-${placeholder}-${val}`;
                const checked = selected.includes(val);

                return (
                  <label
                    key={val}
                    className={styles.msItem}
                    htmlFor={id}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      id={id}
                      type="checkbox"
                      className={styles.msCheck}
                      checked={checked}
                      onChange={() => handleItem(opt)}
                    />
                    <span className={styles.msLabel}>{lab}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <div className={styles.msFooter}>
            <button
              type="button"
              className={styles.msClear}
              disabled={!canClear}
              onClick={() => setSelected([])}
              title="Limpar seleção"
            >
              Limpar
            </button>
            <button
              type="button"
              className={styles.msOk}
              onClick={() => setOpen(false)}
              title="Fechar"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const SidebarMultiSelect = React.memo(function SidebarMultiSelect({
  options = [],
  value,
  onChange,
  placeholder = "Todos",
  title = "Selecione",
  searchable = false,
  searchPlaceholder = "Buscar...",
}) {
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const searchRef = useRef(null);

  const selected = Array.isArray(value) ? value : value ? [value] : [];
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const setSelected = (next) => onChange(Array.isArray(value) ? next : next[0] || "");
  const getVal = useCallback((opt) => (typeof opt === "string" ? opt : opt?.value), []);
  const getLabel = useCallback(
    (opt) => (typeof opt === "string" ? opt : opt?.label ?? opt?.value ?? ""),
    []
  );
  const normalizedOptions = useMemo(
    () =>
      options
        .map((opt) => ({ value: getVal(opt), label: getLabel(opt) }))
        .filter((opt) => opt.value !== undefined && opt.value !== null && opt.value !== ""),
    [getLabel, getVal, options]
  );
  const selectedLabels = useMemo(() => {
    const labels = normalizedOptions
      .filter((opt) => selectedSet.has(opt.value))
      .map((opt) => opt.label)
      .filter(Boolean);
    if (labels.length) return labels;
    return selected.map((item) => String(item ?? "").trim()).filter(Boolean);
  }, [normalizedOptions, selected, selectedSet]);
  const label =
    selectedLabels.length === 0
      ? placeholder
      : selectedLabels.length === 1
      ? selectedLabels[0]
      : `${selectedLabels[0]} +${selectedLabels.length - 1}`;

  useEffect(() => {
    if (!open) return undefined;

    const onDoc = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => {
      if (e.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) {
      setOpenUp(false);
      return;
    }

    const updateDirection = () => {
      const anchorEl = rootRef.current;
      if (!anchorEl) return;
      const rect = anchorEl.getBoundingClientRect();
      const sidebarRect = anchorEl.closest("aside")?.getBoundingClientRect?.() || null;
      const estimatedHeight = searchable ? 344 : 288;
      const spaceBelow = sidebarRect ? sidebarRect.bottom - rect.bottom : window.innerHeight - rect.bottom;
      const spaceAbove = sidebarRect ? rect.top - sidebarRect.top : rect.top;
      setOpenUp(spaceBelow < estimatedHeight && spaceAbove > spaceBelow);
    };

    updateDirection();
    window.addEventListener("resize", updateDirection);
    window.addEventListener("scroll", updateDirection, true);
    return () => {
      window.removeEventListener("resize", updateDirection);
      window.removeEventListener("scroll", updateDirection, true);
    };
  }, [open, searchable]);

  const normalizedQuery = useMemo(() => normalizeSearchText(query), [query]);
  const filteredOptions = useMemo(() => {
    if (!searchable || !normalizedQuery) return normalizedOptions;
    return normalizedOptions.filter((opt) =>
      normalizeSearchText(opt.label).includes(normalizedQuery)
    );
  }, [normalizedOptions, normalizedQuery, searchable]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      return;
    }
    if (!searchable) return undefined;
    const id = window.requestAnimationFrame(() => searchRef.current?.focus());
    return () => window.cancelAnimationFrame(id);
  }, [open, searchable]);

  const handleItem = (opt) => {
    const val = getVal(opt);
    const s = new Set(selected);
    if (s.has(val)) s.delete(val);
    else s.add(val);
    setSelected(Array.from(s));
  };

  const handleClear = () => {
    setSelected([]);
    setQuery("");
  };

  return (
    <div className={styles.msRoot} ref={rootRef}>
      <button
        className={`${styles.msBtn} ${open ? styles.open : ""}`}
        type="button"
        onClick={() => setOpen((v) => !v)}
      >
        <span className={styles.msBtnText}>
          <span className={`${styles.msText} ${selectedLabels.length ? "" : styles.isPlaceholder}`.trim()}>
            {label}
          </span>
          {selectedLabels.length > 1 ? (
            <span className={styles.msMeta}>{selectedLabels.length} selecionados</span>
          ) : null}
        </span>
        <span className={styles.caret}>â–¾</span>
      </button>

      {open && (
        <div className={`${styles.msPanel} ${openUp ? styles.msPanelUp : ""}`.trim()}>
          <div className={styles.msHeader}>
            <div className={styles.msHeaderCopy}>
              <span className={styles.msHeaderTitle}>{title}</span>
              <span className={styles.msHeaderMeta}>
                {selectedLabels.length
                  ? `${selectedLabels.length} marcado${selectedLabels.length > 1 ? "s" : ""}`
                  : "Nenhum marcado"}
              </span>
            </div>
            <button type="button" className={styles.msActionBtn} onClick={handleClear}>
              Limpar
            </button>
          </div>

          {searchable && (
            <div className={styles.msSearchWrap}>
              <input
                ref={searchRef}
                type="text"
                className={styles.msSearch}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
              />
            </div>
          )}

          <div className={styles.msListWrap}>
            <div className={styles.msList}>
              {filteredOptions.map((opt) => {
                const val = getVal(opt);
                const lab = getLabel(opt);
                const checked = selectedSet.has(val);
                return (
                  <button
                    type="button"
                    key={val}
                    className={`${styles.msItem} ${checked ? styles.msItemSelected : ""}`.trim()}
                    onClick={() => handleItem(opt)}
                    aria-pressed={checked}
                  >
                    <span className={styles.msLabel}>{lab}</span>
                  </button>
                );
              })}
              {!filteredOptions.length && (
                <div className={styles.msEmpty}>Nenhum resultado encontrado.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

function SidebarDateDropdown({
  value,
  onChange,
  ariaLabel = "Selecionar data",
}) {
  const rootRef = useRef(null);
  const selectedDate = useMemo(() => parseYMD(value) || parseYMD(todayKeyBR()), [value]);
  const [open, setOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => startOfMonthUTC(selectedDate));

  useEffect(() => {
    if (!open) setViewMonth(startOfMonthUTC(selectedDate));
  }, [open, selectedDate]);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const selectedYMD = formatYMD(selectedDate);
  const todayYMD = todayKeyBR();
  const firstWeekday = viewMonth.getUTCDay();
  const gridStart = addDaysUTC(viewMonth, -firstWeekday);
  const calendarDays = Array.from({ length: 42 }, (_, index) => addDaysUTC(gridStart, index));

  const handlePick = (ymd) => {
    onChange(ymd);
    setOpen(false);
  };

  return (
    <div className={`${styles.dateDropdown} ${open ? styles.dateDropdownOpen : ""}`.trim()} ref={rootRef}>
      <button
        className={styles.dateButton}
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className={styles.dateButtonValue}>{toBRDate(selectedYMD)}</span>
      </button>

      {open && (
        <div className={styles.datePanel} role="dialog" aria-label="Calendário">
          <div className={styles.datePanelHead}>
            <button
              className={styles.dateNav}
              type="button"
              onClick={() => setViewMonth((prev) => addMonthsUTC(prev, -1))}
              aria-label="Mês anterior"
            >
              {"<"}
            </button>
            <div className={styles.datePanelTitle}>{monthLabelPtBR(viewMonth)}</div>
            <button
              className={styles.dateNav}
              type="button"
              onClick={() => setViewMonth((prev) => addMonthsUTC(prev, 1))}
              aria-label="Próximo mês"
            >
              {">"}
            </button>
          </div>

          <div className={styles.dateWeekdays}>
            {DATE_WEEKDAYS.map((weekday, index) => (
              <span key={`${weekday}-${index}`} className={styles.dateWeekday}>
                {weekday}
              </span>
            ))}
          </div>

          <div className={styles.dateGrid}>
            {calendarDays.map((day) => {
              const ymd = formatYMD(day);
              const isOutside = day.getUTCMonth() !== viewMonth.getUTCMonth();
              const isSelected = ymd === selectedYMD;
              const isToday = ymd === todayYMD;

              return (
                <button
                  key={ymd}
                  className={[
                    styles.dateDay,
                    isOutside ? styles.dateDayOutside : "",
                    isSelected ? styles.dateDaySelected : "",
                    isToday ? styles.dateDayToday : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  type="button"
                  onClick={() => handlePick(ymd)}
                  aria-pressed={isSelected}
                >
                  {day.getUTCDate()}
                </button>
              );
            })}
          </div>

          <div className={styles.datePanelFoot}>
            <button
              className={styles.dateAction}
              type="button"
              onClick={() => handlePick(todayYMD)}
            >
              Hoje
            </button>
            <button
              className={`${styles.dateAction} ${styles.dateActionGhost}`}
              type="button"
              onClick={() => setOpen(false)}
            >
              Fechar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
/* ============================================================= */

export default function W3Page({ onBack }) {
  const hojeKey = todayKeyBR();

  const [rows, setRows] = useState([]);
  const [selecionada, setSelecionada] = useState(null);
  const [dataFiltro, setDataFiltro] = useState(hojeKey);
  const [loading, setLoading] = useState(true);
  const [novaOpen, setNovaOpen] = useState(false);

  /* reloginho pros cards */
  const [nowTs, setNowTs] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  /* filtros */
  const [filGerencias, setFilGerencias] = useState([]);
  const [filCoordenadores, setFilCoordenadores] = useState([]);
  const [filAtividades, setFilAtividades] = useState([]);
  const [filAtivos, setFilAtivos] = useState([]);
  const [filStatus, setFilStatus] = useState([]);

  const dayKey = useMemo(
    () => toKeyYYYYMMDD(dataFiltro) || hojeKey,
    [dataFiltro, hojeKey]
  );
  const dayKeyRef = useRef(dayKey);

  useEffect(() => {
    dayKeyRef.current = dayKey;
  }, [dayKey]);

  const carregar = useCallback(() => {
    setLoading(true);

    const k = toKeyYYYYMMDD(dayKey) || hojeKey;
    const { inicioBR, fimBR } = buildDayRangeBR(k);

    const payload = {
      etapa: "wf3",
      include_finalizados: 1,
      data: k,
      dataKey: k,
      dia: k,
      data_inicio: inicioBR,
      data_fim: fimBR,
      inicio: inicioBR,
      fim: fimBR,
      limit: 5000,
      offset: 0,
    };

    listW3(payload)
      .then((list) =>
        setRows((Array.isArray(list) ? list : []).map((item) => normalizeW3RowForState(item)).filter(Boolean))
      )
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [dayKey, hojeKey]);

  const upsertRow = useCallback((incoming) => {
    const nextRow = normalizeW3RowForState(incoming);
    if (!nextRow) return;

    const code = nextRow.codigo_unico != null ? String(nextRow.codigo_unico) : "";
    const id = nextRow.id != null ? String(nextRow.id) : "";
    const rowDay = toKeyYYYYMMDD(nextRow.data || nextRow.day || nextRow.data_key || nextRow.dataKey);
    const currentDay = dayKeyRef.current;

    setRows((prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      const idx = arr.findIndex((item) => {
        const itemCode = item?.codigo_unico != null ? String(item.codigo_unico) : "";
        const itemId = item?.id != null ? String(item.id) : "";
        if (code && itemCode) return itemCode === code;
        if (!code && id && itemId) return itemId === id;
        if (code && !itemCode && id && itemId) return itemId === id;
        return false;
      });

      if (rowDay && rowDay !== currentDay) {
        if (idx < 0) return arr;
        const next = arr.slice();
        next.splice(idx, 1);
        return next;
      }

      if (idx >= 0) {
        const next = arr.slice();
        next[idx] = { ...(arr[idx] || {}), ...nextRow };
        return next;
      }

      return [nextRow, ...arr];
    });
  }, []);

  const removeRowByCodigo = useCallback((codigoOrId) => {
    if (!codigoOrId) return;
    const key = String(codigoOrId);
    setRows((prev) =>
      (Array.isArray(prev) ? prev : []).filter((item) => {
        const itemCode = item?.codigo_unico != null ? String(item.codigo_unico) : "";
        const itemId = item?.id != null ? String(item.id) : "";
        return itemCode !== key && itemId !== key;
      })
    );
  }, []);

  /* carrega na montagem + quando dayKey mudar */
  useEffect(() => {
    carregar();
  }, [carregar]);

  /* sempre que entrar / hoje mudar, reset geral */
  useEffect(() => {
    setDataFiltro(hojeKey);
    setFilGerencias([]);
    setFilCoordenadores([]);
    setFilAtividades([]);
    setFilAtivos([]);
    setFilStatus([]);
  }, [hojeKey]);

/* Realtime WF-3 com patch local e resync só quando necessário */
  useEffect(() => {
    let t = null;
    const rooms = new Set(
      [makeWorkflowAllRoom("wf3"), makeWorkflowDayRoom("wf3", dayKey)].filter(Boolean)
    );

    const refresh = () => {
      if (t) clearTimeout(t);
      t = setTimeout(() => {
        carregar();
        t = null;
      }, 250);
    };

    let mounted = true;
    (async () => {
      try {
        await connectRealtime();
      } catch {}
      if (!mounted) return;
      for (const room of rooms) joinRoom(room);
    })();

    const onW3Changed = (payload) => {
      const row = extractW3FromMsg(payload);
      const codigo = extractW3Codigo(payload);
      const action = extractW3Action(payload).toLowerCase();
      const pDay =
        toKeyYYYYMMDD(row?.data || row?.day || row?.data_key || row?.dataKey) ||
        toKeyYYYYMMDD(payload?.day) ||
        toKeyYYYYMMDD(payload?.data) ||
        toKeyYYYYMMDD(payload?.data_key) ||
        toKeyYYYYMMDD(payload?.dataKey);
      const currentDay = dayKeyRef.current;
      const isDeleteLike =
        action.includes("delete") ||
        action.includes("deleted") ||
        action.includes("remov");

      if (row && typeof row === "object") {
        if (pDay && pDay !== currentDay) {
          refresh();
          return;
        }
        upsertRow(row);
        return;
      }

      if (isDeleteLike && codigo) {
        removeRowByCodigo(codigo);
        return;
      }

      if (codigo && pDay && pDay !== currentDay) {
        removeRowByCodigo(codigo);
        return;
      }

      refresh();
    };

    rtOn(RT.WF3_CHANGED, onW3Changed);
    const onLocal = (event) => onW3Changed(event?.detail);
    window.addEventListener(W3_LOCAL_EVENT, onLocal);
    const onReconnect = () => refresh();
    onRealtimeMeta(RT_META.RECONNECTED, onReconnect);
    return () => {
      mounted = false;
      for (const room of rooms) leaveRoom(room);
      rtOff(RT.WF3_CHANGED, onW3Changed);
      window.removeEventListener(W3_LOCAL_EVENT, onLocal);
      offRealtimeMeta(RT_META.RECONNECTED, onReconnect);
      if (t) clearTimeout(t);
    };
  }, [carregar, dayKey, removeRowByCodigo, upsertRow]);

  /* Status helper */
  const getStatusKey = useCallback((p) => {
    const wf = norm(p?.status_workflow || p?.status_wf3).toLowerCase();
    const fin = norm(p?.status_finalizacao).toLowerCase();

    if (/finaliz/.test(fin) || /finaliz/.test(wf)) return "finalizado";
    if (/reprog/.test(fin) || /reprog/.test(wf)) return "reprogramar";
    if (/execut/.test(fin) || /execut/.test(wf)) return "executado";

    if (/segunda/.test(fin) || fin === "2" || fin === "segunda_previa") return "segunda_previa";
    if (/primeira/.test(fin) || fin === "1" || fin === "primeira_previa") return "primeira_previa";

    if (norm(p?.segunda_previa)) return "segunda_previa";
    if (norm(p?.primeira_previa)) return "primeira_previa";

    if (/andamento/.test(fin) || /andamento/.test(wf)) return "em_andamento";
    return "em_andamento";
  }, []);

  // ✅ REGRA: a tela NÃO "enxerga" status reprogramar
  const rowsVisuais = useMemo(() => {
    return (rows || []).filter((p) => getStatusKey(p) !== "reprogramar");
  }, [rows, getStatusKey]);

  /* ======= OPTIONS ======= */
  const gerenciaOptions = useMemo(
    () => uniq(rowsVisuais.map((p) => normalizeGerenciaLabel(p.gerencia))),
    [rowsVisuais]
  );
  const coordenadorOptions = useMemo(() => uniq(rowsVisuais.map((p) => p.coordenador)), [rowsVisuais]);
  const atividadeOptions = useMemo(() => uniq(rowsVisuais.map((p) => p.atividade)), [rowsVisuais]);
  const ativoOptions = useMemo(() => uniq(rowsVisuais.map((p) => p.ativo)), [rowsVisuais]);

  const statusKeysPresent = useMemo(() => {
    const keys = uniq(rowsVisuais.map((p) => getStatusKey(p)));
    return keys.sort((a, b) => ORDER_STATUS.indexOf(a) - ORDER_STATUS.indexOf(b));
  }, [rowsVisuais, getStatusKey]);

  const statusOptions = useMemo(() => {
    return ORDER_STATUS.filter((k) => statusKeysPresent.includes(k)).map((k) => ({
      value: k,
      label: STATUS_LABEL[k] || k,
    }));
  }, [statusKeysPresent]);

  /* ======= FILTROS ======= */
  const baseFiltradaOutros = useMemo(() => {
    const gset = new Set(filGerencias.map(normUp));
    const cset = new Set(filCoordenadores.map(normUp));
    const aset = new Set(filAtividades.map(normUp));
    const iset = new Set(filAtivos.map(normUp));

    return rowsVisuais.filter((p) => {
      // força exibir apenas o dia selecionado
      const rowDay =
        toKeyYYYYMMDD(p?.data) ||
        toKeyYYYYMMDD(p?.data_key) ||
        toKeyYYYYMMDD(p?.dataKey) ||
        toKeyYYYYMMDD(p?.dia);

      if (rowDay !== dayKey) return false;

      if (gset.size && !gset.has(normalizeGerenciaLabel(p.gerencia))) return false;
      if (cset.size && !cset.has(normUp(p.coordenador))) return false;
      if (aset.size && !aset.has(normUp(p.atividade))) return false;
      if (iset.size && !iset.has(normUp(p.ativo))) return false;

      return true;
    });
  }, [
    rowsVisuais,
    filGerencias,
    filCoordenadores,
    filAtividades,
    filAtivos,
    dayKey,
  ]);

  const visiveis = useMemo(() => {
    const statusSet = new Set(filStatus.map(normUp));
    return !statusSet.size
      ? baseFiltradaOutros
      : baseFiltradaOutros.filter((p) => statusSet.has(normUp(getStatusKey(p))));
  }, [baseFiltradaOutros, filStatus, getStatusKey]);

  const relatorio = useMemo(() => {
    const total = visiveis.length;
    const finalizados = visiveis.reduce(
      (acc, p) => acc + (getStatusKey(p) === "finalizado" ? 1 : 0),
      0
    );
    return { total, finalizados, aExecutar: total - finalizados };
  }, [visiveis, getStatusKey]);

  /* Data */
  const handleDataFiltroChange = (nextValue) => {
    const raw = typeof nextValue === "string" ? nextValue : nextValue?.target?.value;
    const picked = toKeyYYYYMMDD(raw);
    setDataFiltro(picked || hojeKey);
  };

  /* ====== Sidebar: posições escaladas (base 1080) ====== */
  const sidebarRef = useRef(null);
  useLayoutEffect(() => {
    const apply = () => {
      const el = sidebarRef.current;
      if (!el) return;

      const h = el.clientHeight || window.innerHeight;
      const BASE = 1080;
      const s = h / BASE;

      const map = {
        "--y-menu": 46 * s,

        "--y-title-data": (216 - 26) * s,
        "--y-data": 216 * s,
        "--y-title-ger": (312 - 26) * s,
        "--y-ger": 312 * s,
        "--y-title-coord": (408 - 26) * s,
        "--y-coord": 408 * s,
        "--y-title-ati": (504 - 26) * s,
        "--y-ati": 504 * s,
        "--y-title-ativos": (600 - 26) * s,
        "--y-ativos": 600 * s,
        "--y-title-status": (696 - 26) * s,
        "--y-status": 696 * s,
      };

      const CTRL_H = 42;
      const GAP = 12;
      const BOTTOM_MARGIN = 22;

      const yNova = h - BOTTOM_MARGIN - CTRL_H;
      const yLimpar = yNova - GAP - CTRL_H;
      const yMini = yLimpar - GAP - CTRL_H;

      Object.entries(map).forEach(([k, v]) => el.style.setProperty(k, `${Math.round(v)}px`));
      el.style.setProperty("--y-mini", `${Math.round(yMini)}px`);
      el.style.setProperty("--y-limpar", `${Math.round(yLimpar)}px`);
      el.style.setProperty("--y-nova", `${Math.round(yNova)}px`);
    };

    apply();
    window.addEventListener("resize", apply);
    return () => window.removeEventListener("resize", apply);
  }, []);

  const limparFiltros = () => {
    setFilGerencias([]);
    setFilCoordenadores([]);
    setFilAtividades([]);
    setFilAtivos([]);
    setFilStatus([]);
    setDataFiltro(hojeKey);
  };

  return (
    <div className={styles.root}>
      {/* ===== SIDEBAR fixa (sem scroll) ===== */}
      <aside className={styles.sidebar} ref={sidebarRef}>
        <button type="button" className={`${styles.btn} ${styles.abs} ${styles.posMenu}`} onClick={onBack}>
          Menu Principal
        </button>

        {/* Filtrar Data */}
        <div className={`${styles.titleSmall} ${styles.abs} ${styles.posTitleData}`}>
          Filtrar Data
        </div>
        <div className={`${styles.abs} ${styles.posData}`}>
          <SidebarDateDropdown
            value={dataFiltro}
            onChange={handleDataFiltroChange}
            ariaLabel="Selecionar data"
          />
        </div>

        {/* Filtrar Gerências */}
        <div className={`${styles.titleSmall} ${styles.abs} ${styles.posTitleGer}`}>
          Filtrar Gerências
        </div>
        <div className={`${styles.abs} ${styles.posGer}`}>
          <SidebarMultiSelect
            options={gerenciaOptions}
            value={filGerencias}
            onChange={setFilGerencias}
            placeholder="Todas as gerências"
            title="Filtrar Gerências"
            searchable
            searchPlaceholder="Buscar gerência..."
          />
        </div>

        {/* Filtrar Coordenadores */}
        <div className={`${styles.titleSmall} ${styles.abs} ${styles.posTitleCoord}`}>
          Filtrar Coordenadores
        </div>
        <div className={`${styles.abs} ${styles.posCoord}`}>
          <SidebarMultiSelect
            options={coordenadorOptions}
            value={filCoordenadores}
            onChange={setFilCoordenadores}
            placeholder="Todos os coordenadores"
            title="Filtrar Coordenadores"
            searchable
            searchPlaceholder="Buscar coordenador..."
          />
        </div>

        {/* Filtrar Atividades */}
        <div className={`${styles.titleSmall} ${styles.abs} ${styles.posTitleAti}`}>
          Filtrar Atividades
        </div>
        <div className={`${styles.abs} ${styles.posAti}`}>
          <SidebarMultiSelect
            options={atividadeOptions}
            value={filAtividades}
            onChange={setFilAtividades}
            placeholder="Todas as atividades"
            title="Filtrar Atividades"
            searchable
            searchPlaceholder="Buscar atividade..."
          />
        </div>

        {/* Filtrar Ativos */}
        <div className={`${styles.titleSmall} ${styles.abs} ${styles.posTitleAtivos}`}>
          Filtrar Ativos
        </div>
        <div className={`${styles.abs} ${styles.posAtivos}`}>
          <SidebarMultiSelect
            options={ativoOptions}
            value={filAtivos}
            onChange={setFilAtivos}
            placeholder="Todos os ativos"
            title="Filtrar Ativos"
            searchable
            searchPlaceholder="Buscar ativo..."
          />
        </div>

        {/* Filtrar Status */}
        <div className={`${styles.titleSmall} ${styles.abs} ${styles.posTitleStatus}`}>
          Filtrar Status
        </div>
        <div className={`${styles.abs} ${styles.posStatus}`}>
          <SidebarMultiSelect
            options={statusOptions}
            value={filStatus}
            onChange={setFilStatus}
            placeholder="Todos os status"
            title="Filtrar Status"
          />
        </div>

        {/* Minis (linha) */}
        <div className={`${styles.abs} ${styles.miniRow} ${styles.posMiniRow}`}>
          <div className={styles.mini}>
            <div className={styles.miniLbl}>Programado</div>
            <div className={styles.miniVal}>{relatorio.total}</div>
          </div>
          <div className={styles.mini}>
            <div className={styles.miniLbl}>Finalizado</div>
            <div className={styles.miniVal}>{relatorio.finalizados}</div>
          </div>
          <div className={styles.mini}>
            <div className={styles.miniLbl}>Execução</div>
            <div className={styles.miniVal}>{relatorio.aExecutar}</div>
          </div>
        </div>

        {/* Limpar filtros */}
        <button
          type="button"
          className={`${styles.btn} ${styles.btnIcon} ${styles.abs} ${styles.posLimpar}`}
          onClick={limparFiltros}
          title="Limpar filtros"
        >
          <FilterOffIcon size={18} />
          Limpar filtros
        </button>

        {/* Nova Programacao */}
        <button
          type="button"
          className={`${styles.btn} ${styles.abs} ${styles.posNova}`}
          onClick={() => setNovaOpen(true)}
        >
          Nova Programação
        </button>
      </aside>

      {/* ===== CONTEÚDO (rola) ===== */}
      <div className={styles.content}>
        <div className={styles.contentHeader}>
          {/* ✅ sem dia no cabeçalho */}
          <h2 className={styles.title}>Execução & Fechamento</h2>
        </div>

        <div className={styles.contentBody}>
          {visiveis.length === 0 ? (
            <div className={styles.emptyState}>
              {loading ? "Carregando..." : "Nenhum registro."}
            </div>
          ) : (
            <div className={`${styles.grid} ${loading ? styles.gridRefreshing : ""}`}>
              {visiveis.map((p) => (
                <W3Card
                  key={p.codigo_unico || p.id}
                  item={p}
                  nowTs={nowTs}
                  onSelect={(it) => setSelecionada({ ...it, __dataKey: dayKey })}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {selecionada && (
        <W3FichaModal
          open
          data={selecionada}
          onClose={() => setSelecionada(null)}
          onSaved={(updated) => {
            if (updated && typeof updated === "object") upsertRow(updated);
            setSelecionada(null);
          }}
        />
      )}

      <W3NovaProgramar
        open={novaOpen}
        onClose={() => setNovaOpen(false)}
        onCreated={(created) => {
          const row = extractW3FromMsg(created);
          if (row && typeof row === "object") upsertRow(row);
          setNovaOpen(false);
        }}
      />
    </div>
  );
}


